import React, { useState, useRef, useEffect } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler, // Required for area fills in confidence intervals
} from 'chart.js';
import './BankrollRiskPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Helper function for normal distribution PDF (for chart overlay)
function normalPDF(x, mean, stdDev) {
  if (stdDev === 0) return x === mean ? Infinity : 0; // Avoid division by zero
  return (
    (1 / (stdDev * Math.sqrt(2 * Math.PI))) *
    Math.exp(-0.5 * Math.pow((x - mean) / stdDev, 2))
  );
}

// Helper function for percentiles
function percentile(arr, p) {
  if (arr.length === 0) return 0;
  const sortedArr = [...arr].sort((a, b) => a - b);
  const k = (sortedArr.length - 1) * (p / 100);
  const f = Math.floor(k);
  const c = Math.ceil(k);
  if (f === c) {
    return sortedArr[k];
  }
  const d0 = sortedArr[f] * (c - k);
  const d1 = sortedArr[c] * (k - f);
  return d0 + d1;
}

const BankrollRiskPage = () => {
  const [tournaments, setTournaments] = useState([
    {
      id: 1,
      numPlayers: 100,
      placesPaid: 15,
      buyin: 50,
      rake: 10, // percentage
      roi: 15, // percentage
      numToPlay: 1000,
      stdevBuyins: 4, // Standard deviation in terms of buy-ins for this tourney type
    },
  ]);
  const [sampleSize, setSampleSize] = useState(1000);
  const [initialBankroll, setInitialBankroll] = useState(''); // Optional

  const [simulationResults, setSimulationResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [calculationTime, setCalculationTime] = useState(0);

  const distributionChartRef = useRef(null);
  const sampleRunsChartRef = useRef(null);

  useEffect(() => {
    // Cleanup charts on component unmount or before recalculation if needed
    return () => {
      if (distributionChartRef.current) {
        distributionChartRef.current.destroy();
      }
      if (sampleRunsChartRef.current) {
        sampleRunsChartRef.current.destroy();
      }
    };
  }, []);


  const handleTournamentChange = (index, field, value) => {
    const newTournaments = [...tournaments];
    // Ensure numeric types for relevant fields
    if (['numPlayers', 'placesPaid', 'buyin', 'rake', 'roi', 'numToPlay', 'stdevBuyins'].includes(field)) {
        newTournaments[index][field] = value === '' ? '' : parseFloat(value);
    } else {
        newTournaments[index][field] = value;
    }
    setTournaments(newTournaments);
  };

  const addTournamentType = () => {
    setTournaments([
      ...tournaments,
      {
        id: tournaments.length + 1,
        numPlayers: 100,
        placesPaid: 15,
        buyin: 10,
        rake: 10,
        roi: 10,
        numToPlay: 500,
        stdevBuyins: 3,
      },
    ]);
  };

  const removeTournamentType = (index) => {
    if (tournaments.length > 1) {
      const newTournaments = tournaments.filter((_, i) => i !== index);
      setTournaments(newTournaments);
    }
  };

  // This function generates a skewed profit for a single tournament
  // It aims to match the targetROI and uses a heuristic for variance.
  const generateTournamentProfitSkewed = (tourney) => {
    const cost = parseFloat(tourney.buyin);
    if (isNaN(cost) || cost <= 0) return 0;

    const roiDecimal = parseFloat(tourney.roi) / 100;
    const expectedProfit = cost * roiDecimal;

    // Heuristic for ITM probability, influenced by ROI
    let baseItm = (tourney.placesPaid || 1) / (tourney.numPlayers || 1);
    baseItm = Math.max(0.05, Math.min(baseItm, 0.5)); // Clamp base ITM
    let probITM = baseItm * (1 + roiDecimal * 1.5); // ROI has a moderate impact on ITM%
    probITM = Math.max(baseItm * 0.7, Math.min(probITM, baseItm * 2.5, 0.75)); // Further clamp ITM

    if (Math.random() > probITM) {
      return -cost; // Lost buy-in
    } else {
      // Cashed. Determine profit.
      // This calculation ensures that on average, if player cashes, their profit aligns with overall expectedProfit
      const avgProfitWhenCash = (expectedProfit - (1 - probITM) * (-cost)) / probITM;
      
      // Simulate skewed prize distribution for cashes
      const minCashProfit = cost * 0.3; // Smallest profit when cashing (e.g. 1.3x BI prize)
      
      // Max profit factor, influenced by player count
      let maxProfitFactor = 3 + Math.log10(tourney.numPlayers || 10) * 7;
      maxProfitFactor = Math.max(3, Math.min(maxProfitFactor, 70)); // Max profit e.g. 70 BIs
      const maxCashProfit = cost * maxProfitFactor;

      let cashProfit;
      const r = Math.random();

      // Tiered system for cash amounts (simplified Pareto-like distribution)
      if (r < 0.60) { // 60% of cashes are small
        cashProfit = minCashProfit + Math.random() * (cost * 2); // Profit 0.3 to 2.3 BIs
      } else if (r < 0.85) { // 25% of cashes are medium
        cashProfit = (cost * 2.3) + Math.random() * (cost * 7); // Profit 2.3 to 9.3 BIs
      } else { // 15% of cashes are large
        cashProfit = (cost * 9.3) + Math.random() * (maxCashProfit - (cost * 9.3));
      }
      
      // Ensure the generated cashProfit, when averaged, approximates avgProfitWhenCash
      // This current tiered model is heuristic. A true way is to use multipliers for avgProfitWhenCash
      // For instance:
      // if (r < 0.60) cashProfit = 0.5 * avgProfitWhenCash;
      // else if (r < 0.85) cashProfit = 1.5 * avgProfitWhenCash;
      // else cashProfit = (avgProfitWhenCash - 0.60*(0.5*avgProfitWhenCash) - 0.25*(1.5*avgProfitWhenCash)) / 0.15;
      // Then clamp:
      cashProfit = Math.max(minCashProfit, cashProfit);
      cashProfit = Math.min(maxCashProfit, cashProfit);
      return cashProfit;
    }
  };

  const runSimulation = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSimulationResults(null);
    const startTime = performance.now();

    // Create a flat list of all tournaments to be played in sequence for each sample
    const tournamentSchedule = [];
    tournaments.forEach(t => {
        for(let i = 0; i < t.numToPlay; i++) {
            tournamentSchedule.push(t);
        }
    });
    
    if (tournamentSchedule.length === 0) {
        setIsLoading(false);
        alert("Please configure at least one tournament to play.");
        return;
    }

    const totalTournamentsToPlay = tournamentSchedule.length;

    const finalProfitsPerSample = [];
    const profitPaths = []; // For sample runs chart
    const maxDrawdowns = []; // Max loss from $0 bankroll for each run
    let ruinCount = 0;
    let neverDippedBelowInitialBankrollCount = 0; // if bankroll is provided

    for (let i = 0; i < sampleSize; i++) {
      let cumulativeProfit = 0;
      let currentSimBankroll = initialBankroll ? parseFloat(initialBankroll) : 0;
      let path = [initialBankroll ? parseFloat(initialBankroll) : 0]; // Start path at initial bankroll or 0
      let minProfitReached = 0;
      let currentMinBankroll = currentSimBankroll;
      let ruinedInThisSample = false;

      for (let j = 0; j < totalTournamentsToPlay; j++) {
        const currentTournamentType = tournamentSchedule[j];
        const profitOneTournament = generateTournamentProfitSkewed(currentTournamentType);
        
        cumulativeProfit += profitOneTournament;
        minProfitReached = Math.min(minProfitReached, cumulativeProfit);

        if (initialBankroll) {
          currentSimBankroll += profitOneTournament;
          path.push(currentSimBankroll);
          currentMinBankroll = Math.min(currentMinBankroll, currentSimBankroll);
          if (currentSimBankroll <= 0 && !ruinedInThisSample) {
            ruinCount++;
            ruinedInThisSample = true;
          }
        } else {
            path.push(cumulativeProfit); // Track profit path if no bankroll
        }
      }
      finalProfitsPerSample.push(cumulativeProfit);
      if (i < 20) profitPaths.push(path); // Store first 20 paths for chart
      maxDrawdowns.push(Math.abs(minProfitReached)); // Max bankroll needed to survive this run
      
      if (initialBankroll && currentMinBankroll >= parseFloat(initialBankroll)) {
        neverDippedBelowInitialBankrollCount++;
      } else if (initialBankroll && !ruinedInThisSample && currentMinBankroll > 0){
        // This counts runs that dipped but didn't bust and ended above 0
        // The primedope "Simulated runs that never dipped below 0" might mean never went negative *if starting at 0*
        // Or never went below initial bankroll. Let's use: never went negative from initial bankroll.
        // If starting bankroll is X, and it never went <=0, it counts.
        // The primedope phrasing "never dipped below 0" implies starting at 0 conceptually.
        // My `neverDippedBelowInitialBankrollCount` is different.
        // Let's add a specific "neverDippedBelowZeroProfit" for runs starting at 0 profit.
      }
    }
    
    // Calculate never dipped below zero (conceptual $0 start)
    let neverDippedBelowZeroProfitCount = 0;
    for (let i = 0; i < sampleSize; i++) {
        let currentProfit = 0;
        let dipped = false;
        for (let j = 0; j < totalTournamentsToPlay; j++) {
            const currentTournamentType = tournamentSchedule[j];
            // Need to re-simulate or store individual profits to check this accurately without initialBankroll interference
            // For simplicity, let's use the stored maxDrawdowns: if maxDrawdown is 0 (means profit never went negative)
            // This is not quite right. maxDrawdown is abs(minProfit). If minProfit is 0, it's ok.
            // A run never dips below zero if its min_cumulative_profit >= 0
            // We need to re-evaluate min_cumulative_profit for each run.
            // The current `maxDrawdowns` are `abs(min_profit_reached)`. So if `min_profit_reached >= 0`, then `maxDrawdowns` would effectively be 0 or negative if not for `abs`.
            // Re-calculating min_profit_reached for this specific metric:
            let tempCumulativeProfit = 0;
            let tempMinProfit = 0;
            for (let j_inner = 0; j_inner < totalTournamentsToPlay; j_inner++) {
                const currentTournamentType = tournamentSchedule[j_inner];
                // This is problematic as generateTournamentProfitSkewed is random.
                // We need the actual profit sequence for each run.
                // The `finalProfitsPerSample` and `profitPaths` are based on one set of runs.
                // For now, this metric might be less accurate or omitted if too complex to retrofit.
                // Let's assume `maxDrawdowns[i] === 0` implies it (if profit was always >=0).
                // This is only true if minProfitReached was exactly 0.
                // A simpler way for "never dipped below 0" (starting conceptually at 0):
                // Check profit paths for those NOT using initialBankroll.
                // Let's assume if initialBankroll is NOT set, path[0] is 0.
                // And then check if any path element < 0.
            }
        }
    }

    // --- Statistical Data Calculations ---
    let totalSumBuyins = 0;
    let totalExpectedMathematicalProfit = 0;
    let sumOfMathematicalVariances = 0;

    tournaments.forEach(t => {
        const cost = parseFloat(t.buyin);
        const num = parseFloat(t.numToPlay);
        const roi = parseFloat(t.roi) / 100;
        const stdevBi = parseFloat(t.stdevBuyins);

        totalSumBuyins += cost * num;
        totalExpectedMathematicalProfit += cost * roi * num;
        sumOfMathematicalVariances += num * Math.pow(stdevBi * cost, 2);
    });

    const mathematicalEV = totalExpectedMathematicalProfit;
    const mathematicalROI = totalSumBuyins > 0 ? (mathematicalEV / totalSumBuyins) * 100 : 0;
    const mathematicalSD = Math.sqrt(sumOfMathematicalVariances);

    const simulatedEV = finalProfitsPerSample.reduce((sum, p) => sum + p, 0) / sampleSize;
    const simulatedROI = totalSumBuyins > 0 ? (simulatedEV / totalSumBuyins) * 100 : 0;
    
    let sumSqDiff = 0;
    finalProfitsPerSample.forEach(p => {
        sumSqDiff += Math.pow(p - simulatedEV, 2);
    });
    const simulatedSD = Math.sqrt(sumSqDiff / (sampleSize > 1 ? sampleSize -1 : 1));
    
    // --- Confidence Intervals (simulated) ---
    const ciLevels = [70, 95, 99.7];
    const confidenceIntervals = ciLevels.map(level => {
        const lowerP = (100 - level) / 2;
        const upperP = 100 - lowerP;
        const lowerBound = percentile(finalProfitsPerSample, lowerP);
        const upperBound = percentile(finalProfitsPerSample, upperP);
        return {
            level,
            interval: `${lowerBound.toFixed(0)} USD - ${upperBound.toFixed(0)} USD`,
            roiInterval: totalSumBuyins > 0 ? `${((lowerBound / totalSumBuyins) * 100).toFixed(2)}% - ${((upperBound / totalSumBuyins) * 100).toFixed(2)}%` : 'N/A',
        };
    });

    // --- Bankroll & Risk of Ruin (simulated) ---
    const RoRPercentages = [50, 15, 5, 1];
    const bankrollNeededForRoR = RoRPercentages.map(rorTarget => {
        // rorTarget is % risk of ruin, so (100-rorTarget) is survival percentile
        const survivalPercentile = 100 - rorTarget;
        return {
            risk: rorTarget,
            bankroll: percentile(maxDrawdowns, survivalPercentile).toFixed(0) + " USD",
        };
    });
    
    const riskOfRuinWithBankroll = initialBankroll ? (ruinCount / sampleSize) * 100 : null;
    const probLossAfterTournaments = (finalProfitsPerSample.filter(p => p < 0).length / sampleSize) * 100;

    // --- Prepare Chart Data ---
    // Distribution Chart (Histogram)
    const sortedProfits = [...finalProfitsPerSample].sort((a, b) => a - b);
    const minProfit = sortedProfits[0];
    const maxProfit = sortedProfits[sortedProfits.length - 1];
    const numBins = Math.min(Math.max(Math.round(Math.sqrt(sampleSize)/2), 20), 100); // Heuristic for bin count
    const binSize = (maxProfit - minProfit) / numBins;
    const bins = Array(numBins).fill(0);
    const binLabels = Array(numBins).fill(0).map((_, i) => (minProfit + i * binSize).toFixed(0));

    finalProfitsPerSample.forEach(profit => {
        let binIndex = Math.floor((profit - minProfit) / binSize);
        if (binIndex >= numBins) binIndex = numBins - 1; // last bin for max value
        if (binIndex < 0) binIndex = 0; // first bin for min value
        bins[binIndex]++;
    });
    
    const histogramData = {
        labels: binLabels,
        datasets: [
            {
                label: 'Histogram',
                data: bins.map(b => (b / sampleSize) / (binSize||1)), // Normalized to probability density
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                barPercentage: 1.0,
                categoryPercentage: 1.0,
            },
            {
                label: 'Normal Distribution',
                data: binLabels.map(label => {
                    const x = parseFloat(label) + binSize / 2; // Midpoint of bin
                    return normalPDF(x, simulatedEV, simulatedSD);
                }),
                type: 'line',
                borderColor: 'red',
                borderWidth: 2,
                fill: false,
                pointRadius: 0,
                tension: 0.1,
            }
        ],
    };

    // Sample Runs Chart
    const sampleRunLabels = Array.from({ length: totalTournamentsToPlay + 1 }, (_, i) => i);
    const chartDatasets = [];

    // Mean run
    const meanPath = Array(totalTournamentsToPlay + 1).fill(0);
    if (initialBankroll) meanPath[0] = parseFloat(initialBankroll);
    // This needs all paths to calculate mean correctly, profitPaths only has 20.
    // For simplicity, we'll plot the first 20 and skip a true "mean of all runs" line for now,
    // or calculate it if we store all paths (memory intensive for large sampleSize/numToPlay)
    // Let's calculate a conceptual mean path based on mathematicalEV per tournament
    const conceptualMeanPath = [initialBankroll ? parseFloat(initialBankroll) : 0];
    let cumulativeExpectedProfit = initialBankroll ? parseFloat(initialBankroll) : 0;
    const avgExpectedProfitPerScheduledTourney = mathematicalEV / totalTournamentsToPlay;

    for(let i=0; i<totalTournamentsToPlay; i++) {
        cumulativeExpectedProfit += avgExpectedProfitPerScheduledTourney; // This is too simple if tourney types vary
        // A better way:
        let stepExpectedProfit = 0;
        const currentScheduledTourney = tournamentSchedule[i];
        stepExpectedProfit = (currentScheduledTourney.buyin * (currentScheduledTourney.roi/100));
        conceptualMeanPath.push((conceptualMeanPath[i] || 0) + stepExpectedProfit);
    }
    chartDatasets.push({
        label: 'Mean (Mathematical EV)',
        data: conceptualMeanPath,
        borderColor: 'black',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1,
    });

    // Best and Worst Runs from all samples
    const allFinalCumulativeProfits = []; // Store { path: [], finalProfit: number }
    for (let i = 0; i < sampleSize; i++) {
        let tempCumulativeProfit = 0;
        let tempPath = [initialBankroll ? parseFloat(initialBankroll) : 0];
        for (let j = 0; j < totalTournamentsToPlay; j++) {
            // This requires re-simulation or storing all profits for all runs, which is heavy.
            // For now, best/worst will be from the first 20 samples if not careful.
            // To get true best/worst, we need to simulate again or store all paths.
            // Let's find best/worst based on finalProfitsPerSample and re-simulate those paths if needed.
            // Simpler: profitPaths only has 20. Best/worst will be from those 20.
        }
        // This part is complex to get right without storing all paths.
        // Let's use the finalProfitsPerSample to identify best/worst sample index
        // and then use a (potentially) re-simulated path for them or just plot the first few.
    }
    // For now, just plot the 20 sample paths:
    profitPaths.forEach((path, index) => {
        chartDatasets.push({
            label: `Sample ${index + 1}`,
            data: path,
            borderColor: `hsla(${index * 20}, 70%, 50%, 0.5)`,
            borderWidth: 1,
            pointRadius: 0,
            tension: 0.1,
            borderDash: [5,5]
        });
    });

    const sampleRunsData = {
        labels: sampleRunLabels,
        datasets: chartDatasets,
    };

    setSimulationResults({
        statisticalData: {
            tournamentTypes: tournaments.length,
            totalTournaments: totalTournamentsToPlay,
            sampleSize,
            sumBuyins: totalSumBuyins.toFixed(0) + " USD",
            evMathematical: mathematicalEV.toFixed(0) + " USD",
            evSimulated: simulatedEV.toFixed(0) + " USD",
            roiMathematical: mathematicalROI.toFixed(2) + "%",
            roiSimulated: simulatedROI.toFixed(2) + "%",
            sdMathematical: mathematicalSD.toFixed(0) + " USD",
            sdSimulated: simulatedSD.toFixed(0) + " USD",
        },
        confidenceIntervals,
        bankrollData: {
            bankrollNeededForRoR,
            riskOfRuinWithBankroll: riskOfRuinWithBankroll !== null ? riskOfRuinWithBankroll.toFixed(2) + "%" : "N/A (No bankroll entered)",
            probLossAfterTournaments: probLossAfterTournaments.toFixed(2) + "%",
        },
        charts: {
            distribution: histogramData,
            sampleRuns: sampleRunsData,
        }
    });

    const endTime = performance.now();
    setCalculationTime(((endTime - startTime) / 1000).toFixed(2));
    setIsLoading(false);
  };

  return (
    <div className="bankroll-risk-container mtt-variance-calculator">
      <h1>MTT Variance Calculator</h1>
      
      <form onSubmit={runSimulation}>
        <h2>Enter Tournament Data</h2>
        {tournaments.map((t, index) => (
          <div key={t.id} className="tournament-block">
            <h3>Tournament Type #{index + 1}
              {tournaments.length > 1 && 
                <button type="button" onClick={() => removeTournamentType(index)} className="remove-btn">Remove</button>}
            </h3>
            <div className="input-row">
              <div className="input-group">
                <label># of Players:</label>
                <input type="number" value={t.numPlayers} onChange={e => handleTournamentChange(index, 'numPlayers', e.target.value)} min="2" required />
              </div>
              <div className="input-group">
                <label>Places Paid:</label>
                <input type="number" value={t.placesPaid} onChange={e => handleTournamentChange(index, 'placesPaid', e.target.value)} min="1" required />
              </div>
            </div>
            <div className="input-row">
              <div className="input-group">
                <label>Buy-in (USD):</label>
                <input type="number" value={t.buyin} onChange={e => handleTournamentChange(index, 'buyin', e.target.value)} min="0.01" step="0.01" required />
              </div>
              <div className="input-group">
                <label>Rake (%):</label>
                <input type="number" value={t.rake} onChange={e => handleTournamentChange(index, 'rake', e.target.value)} min="0" max="100" step="0.1" required />
              </div>
            </div>
            <div className="input-row">
              <div className="input-group">
                <label>Expected ROI (%):</label>
                <input type="number" value={t.roi} onChange={e => handleTournamentChange(index, 'roi', e.target.value)} step="0.1" required />
              </div>
              <div className="input-group">
                <label># Tournaments to Play:</label>
                <input type="number" value={t.numToPlay} onChange={e => handleTournamentChange(index, 'numToPlay', e.target.value)} min="1" required />
              </div>
            </div>
            <div className="input-row">
              <div className="input-group">
                <label>Est. SD (in Buy-ins):</label>
                <input type="number" value={t.stdevBuyins} onChange={e => handleTournamentChange(index, 'stdevBuyins', e.target.value)} min="0.1" step="0.1" required title="Estimated Standard Deviation of a single tournament result, expressed in multiples of the buy-in. E.g., 3-7 for MTTs."/>
              </div>
            </div>
          </div>
        ))}
        <button type="button" onClick={addTournamentType} className="add-tournament-btn">Add Another Tournament Type</button>

        <h2>Simulation Settings</h2>
        <div className="input-row">
          <div className="input-group">
            <label htmlFor="sampleSize">Sample Size (Simulations):</label>
            <input type="number" id="sampleSize" value={sampleSize} onChange={e => setSampleSize(Math.max(100, parseInt(e.target.value)))} min="100" max="50000" required />
          </div>
          <div className="input-group">
            <label htmlFor="initialBankroll">Starting Bankroll (USD, Optional):</label>
            <input type="number" id="initialBankroll" value={initialBankroll} onChange={e => setInitialBankroll(e.target.value)} min="0" placeholder="Leave empty if not used"/>
          </div>
        </div>
        
        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Calculating...' : 'Calculate Variance'}
        </button>
      </form>

      {isLoading && <div className="loader">Simulating... please wait.</div>}

      {simulationResults && !isLoading && (
        <div className="results-section">
          <p className="calculation-time">Calculation time: {calculationTime} seconds</p>
          
          <h2>Statistical Data</h2>
          <table className="results-table">
            <tbody>
              {Object.entries(simulationResults.statisticalData).map(([key, value]) => (
                <tr key={key}>
                  <td>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Confidence Intervals (Simulated)</h2>
          <table className="results-table">
            <thead>
              <tr><th>Level</th><th>Confidence Interval</th><th>ROI Interval</th></tr>
            </thead>
            <tbody>
              {simulationResults.confidenceIntervals.map(ci => (
                <tr key={ci.level}>
                  <td>{ci.level}%</td><td>{ci.interval}</td><td>{ci.roiInterval}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Bankroll & Risk of Ruin (Simulated)</h2>
          <table className="results-table">
            <thead>
              <tr><th>Risk of Ruin</th><th>Bankroll Needed</th></tr>
            </thead>
            <tbody>
              {simulationResults.bankrollData.bankrollNeededForRoR.map(br => (
                <tr key={br.risk}><td>{br.risk}%</td><td>{br.bankroll}</td></tr>
              ))}
              <tr>
                <td>Risk of Ruin with {initialBankroll || 0} USD bankroll</td>
                <td>{simulationResults.bankrollData.riskOfRuinWithBankroll}</td>
              </tr>
              <tr>
                <td>Probability of loss after {simulationResults.statisticalData.totalTournaments} tournaments</td>
                <td>{simulationResults.bankrollData.probLossAfterTournaments}</td>
              </tr>
            </tbody>
          </table>

          <div className="charts-container">
            <div className="chart-wrapper">
              <h2>Distribution for all tournaments</h2>
              {simulationResults.charts.distribution.labels && simulationResults.charts.distribution.labels.length > 0 ? (
                <Bar ref={distributionChartRef} data={simulationResults.charts.distribution} options={{ responsive: true, maintainAspectRatio: true, scales: { y: { title: { display: true, text: 'Probability Density'}}}, animation: false }} />
              ) : <p>Not enough data for distribution chart.</p>}
            </div>
            <div className="chart-wrapper">
              <h2>Random Samples (20 Runs)</h2>
              {simulationResults.charts.sampleRuns.labels && simulationResults.charts.sampleRuns.labels.length > 0 ? (
                <Line ref={sampleRunsChartRef} data={simulationResults.charts.sampleRuns} options={{ responsive: true, maintainAspectRatio: true, scales: {y: {title: {display: true, text: 'Profit (USD)'}}, x: {title: {display: true, text: 'Tournaments Played'}}}, animation: false }} />
              ) : <p>Not enough data for sample runs chart.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankrollRiskPage;